import apiClient from '@/lib/apiClient';
import { buildListParams, extractPaginatedData } from '@/shared/utils/queryUtils';

const BASE = '/admin/room-ads';
const USERS_BASE = '/admin/users';

export const roomAdApi = {
  list: async (params: any) => {
    const queryParams = buildListParams(params);
    const res = await apiClient.get(BASE, { params: queryParams });
    return extractPaginatedData(res);
  },

  getById: async (id: any) => {
    const res = await apiClient.get(`${BASE}/${id}`);
    return res.data?.data;
  },

  create: async (data: any) => {
    const res = await apiClient.post(BASE, data);
    return res.data?.data;
  },

  update: async (id: any, data: any) => {
    const res = await apiClient.patch(`${BASE}/${id}`, data);
    return res.data?.data;
  },

  delete: async (id: any) => {
    const res = await apiClient.delete(`${BASE}/${id}`);
    return res.data;
  },

  listUsers: async ({ page = 0, size = 20, firstName = '', lastName = '', email = '', phoneNumber = '' } = {}) => {
    const res = await apiClient.get(USERS_BASE, {
      params: { page, size, firstName, lastName, email, phoneNumber, isDeleted: false }
    });
    return extractPaginatedData(res);
  },

  searchUsers: async (search: string = '', page: number = 0, size: number = 20) => {
    // Search across firstName, lastName, email, and phoneNumber
    const res = await apiClient.get(USERS_BASE, {
      params: {
        page,
        size,
        firstName: search,
        lastName: search,
        email: search,
        phoneNumber: search,
        isDeleted: false
      }
    });
    return extractPaginatedData(res);
  },

  listImages: async (adId: number | string) => {
    const res = await apiClient.get(`${BASE}/${adId}/images`);
    return (res.data?.data ?? []) as AdImageDTO[];
  },

  uploadImages: async (
    adId: number | string,
    files: File[],
    onProgress?: (pct: number) => void,
  ): Promise<AdImageDTO[]> => {
    const fd = new FormData();
    files.forEach((f) => fd.append('images', f, f.name));
    const res = await apiClient.post(`${BASE}/${adId}/images`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (!onProgress || !e.total) return;
        onProgress(Math.round((e.loaded * 100) / e.total));
      },
    });
    return (res.data?.data ?? []) as AdImageDTO[];
  },

  uploadVideos: async (
    adId: number | string,
    files: File[],
    onProgress?: (pct: number) => void,
  ): Promise<AdImageDTO[]> => {
    const fd = new FormData();
    files.forEach((f) => fd.append('videos', f, f.name));
    const res = await apiClient.post(`${BASE}/${adId}/images/videos`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (!onProgress || !e.total) return;
        onProgress(Math.round((e.loaded * 100) / e.total));
      },
    });
    return (res.data?.data ?? []) as AdImageDTO[];
  },

  deleteImage: async (adId: number | string, imageId: number) => {
    await apiClient.delete(`${BASE}/${adId}/images/${imageId}`);
  },

  setPrimaryImage: async (adId: number | string, imageId: number): Promise<AdImageDTO[]> => {
    const res = await apiClient.post(`${BASE}/${adId}/images/${imageId}/primary`);
    return (res.data?.data ?? []) as AdImageDTO[];
  },
};

export type AdImageDTO = {
  id: number;
  imageUrl: string;
  position: number;
  isPrimary: boolean;
  mediaType?: 'IMAGE' | 'VIDEO';
};


